import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MIN_DURATION_SECS,
  SILENCE_DURATION_MS,
  SLACK_ID_WHITELIST,
} from '@/utils/constants';
import { SessionPhase } from '../components/ui/scenarioinfo';
import api from '@/utils/ky';// Assuming you have an api utility

export const log = (event: string, data?: any) => {
  if (import.meta.env.DEV) {
    console.log(`[${new Date().toISOString()}] ${event}`, data || '');
  }
};

const CONSTANTS = {
  MIN_INTERRUPTION_DURATION_MS: 500,
  INTRO_AUTO_COMPLETE_TIMEOUT_MS: 15000,
  INTERRUPTION_WORDS: ['hello', 'hey', 'hi', 'excuse me', 'stop'],
  DEBUG: import.meta.env.DEV,
  SPEAKING_DEBOUNCE_MS: 100,
};

const MAX_RETRY_ATTEMPTS = 3;

export const useRolePlayRTC = ({
  sessionId,
  rolePlaySessionRef,
  startTimeRef,
  userSlackId = undefined,
  setSessionPhase,
  setError,
  setIsAISpeaking,
  setIsIntroCompleted,
  endSession,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [items, setItems] = useState<any[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isBadRequest, setIsBadRequest] = useState(false);
  const [messages, setMessages] = useState([]);

  const isIntroCompletedRef = useRef(false);
  const activeAudioTracksRef = useRef(new Set());
  const introResponseIdRef = useRef<string | null>(null);
  const isIntroMessageCompleteRef = useRef(false);
  const introTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTranscriptRef = useRef<string>('');
  const itemsRef = useRef(items);
  const isSkipEnabledRef = useRef(false);
  const speakingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const practiceStartedRef = useRef(false);

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);

  useEffect(() => {
    if (!practiceStartedRef.current) return;
    
    // Only set initial timeout for skip enable
    setTimeout(() => {
      isSkipEnabledRef.current = true;
    }, 3000);
  }, [practiceStartedRef.current]);

  const updateAISpeakingState = useCallback(() => {
    const isSpeaking = activeAudioTracksRef.current.size > 0;
    log('activeAudioTracksRef.current', activeAudioTracksRef.current);
    setIsAISpeaking(isSpeaking);
    return isSpeaking;
  }, [setIsAISpeaking]);

  const connectConversation = useCallback(async () => {
    if (isLoading || 
        peerConnectionRef.current?.connectionState === 'connected') {
      return;
    }
    
    try {
      log('Connecting Conversation');
      setIsLoading(true);
      setError(null);

      // Ensure skip is disabled when starting new connection
      isSkipEnabledRef.current = false;
      practiceStartedRef.current = true;

      // WebRTC connection setup
      const resp = await api.post('realtime/api/get-ephemeral-token');
      const { token: ephemeralKey } = await resp.json<{token}>();

      const pc = new RTCPeerConnection();
      peerConnectionRef.current = pc;

      startTimeRef.current = Date.now();
      setIsConnected(true);
      setIsBadRequest(false);

      const audioEl = new Audio();
      audioEl.autoplay = true;
      pc.ontrack = (e) => {
        audioEl.srcObject = e.streams[0];
      };

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      const dc = pc.createDataChannel('oai-events');
      dataChannelRef.current = dc;
          
        dc.onopen = () => {
          // dc.send(JSON.stringify({
          //   type: 'session.update',
          //   content: {
          //     model: 'gpt-4o',
          //     temperature: 0.7
          //   }
          // }));

          // 5. Send conversation starter
          dc.send(JSON.stringify({
            type: 'conversation.item.create',
            item: {
              type: "message",
              role: 'user',
              content: [
                {  type: "input_text", text: isIntro ? fullInstructions : 'Hello' }
            ]
            }
          }));

        // 3. Send conversation starter
        dc.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type: 'message',
            role: 'user',
            content: [
              { 
                type: 'input_text',
                text: isIntro ? 'Please introduce the role-play scenario' : 'Hello'
              }
            ]
          }
        }));

        }
      
          // Setup message handler after channel is ready
      dc.onmessage = (e) => {
        const evt = JSON.parse(e.data);
        setMessages((prev) => [...prev, evt]);
        handleWebRTCMessage(evt);
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

       // 7. Fetch SDP answer from OpenAI
      const baseUrl = 'https://api.openai.com/v1/realtime';
      const model = 'gpt-4o-realtime-preview-2024-12-17';
      const sdpResp = await fetch(`${baseUrl}?model=${model}`, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${ephemeralKey}`,
          'Content-Type': 'application/sdp',
        },
      });

      const answerSDP = await sdpResp.text();
      const answer: any = { type: 'answer', sdp: answerSDP };
      await pc.setRemoteDescription(answer);


      const isIntro = !isIntroCompletedRef.current;
      log('Configuring Session', { isIntro });
      log('isIntro && isSkipEnabled', {
        isIntro,
        isSkipEnabled: isSkipEnabledRef.current,
      });
      if (isIntro) {
        setSessionPhase(SessionPhase.INTRO);
      } else {
        setSessionPhase(SessionPhase.MAIN_CONVERSATION);
      }

      const sessionData = rolePlaySessionRef.current;
      if (!sessionData) {
        throw new Error('Session data not found');
      }

      const instructions = isIntro
        ? sessionData.voiceInstructions?.intro
        : sessionData.voiceInstructions?.chat;
      let fullInstructions = instructions ?? '';

      if (isIntro) {
        fullInstructions += `
        manager full name: ${sessionData.user.name}
        role-play scenario: ${sessionData.scenario.title}
        role-play description: ${sessionData.scenario.summary}
        role-play objective: ${sessionData.scenario.objective}
        role-play framework: ${sessionData.framework.title}
        This is the introduction phase. Give a clear, complete introduction then wait for the system to transition to the main conversation.
        `;
      } else {
        fullInstructions += `
        manager full name: ${sessionData.user.name}
        this is the main role-play conversation
        `;
        if (sessionData.aiConfig?.initialPrompt) {
          fullInstructions += `
          IMPORTANT: ${sessionData.aiConfig.initialPrompt}
          `;
        }
      }

      log(`Instructions: ${fullInstructions}`);


      if (isIntro) {
        setTimeout(() => {
          console.log('Enabling skip after delay');
          isSkipEnabledRef.current = true;
        }, 3000);
      }
    } catch (err: any) {
      log('Connection Error', err);
      setError(err.message || 'Failed to connect to AI Coach');
      setIsConnected(false);
      isSkipEnabledRef.current = false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const onSkipIntro = useCallback(async () => {
    log('manually skipping intro');
    setSessionPhase(SessionPhase.MAIN_CONVERSATION);

    try {
      if (!isIntroCompletedRef.current) {
        introResponseIdRef.current = null;
        isIntroMessageCompleteRef.current = false;

        isIntroCompletedRef.current = true;
        setIsIntroCompleted(true);

        await connectConversation();

        log('Successfully transitioned to main conversation');
      }
    } catch (err) {
      log('Error during intro skip:', err);
      await connectConversation();
    }
  }, [connectConversation]);

  const handleWebRTCMessage = (message: any) => {
    switch (message.type) {
      case 'response_created':
        handleResponseCreated(message);
        break;
      case 'output_item_done':
        handleOutputItemDone(message);
        break;
      case 'response_done':
        handleResponseDone(message);
        break;
      case 'audio_transcription_completed':
        handleAudioTranscriptionCompleted(message);
        break;
      case 'conversation_interrupted':
        handleConversationInterrupted();
        break;
      case 'conversation_updated':
        handleConversationUpdated(message);
        break;
      case 'error':
        handleError(message);
        break;
      default:
        log('Unknown message type:', message.type);
    }
  };

  const handleResponseCreated = (message: any) => {
    log('Response Created', {
      responseId: message.response.id,
      isIntro: !isIntroCompletedRef.current,
    });

    if (!isIntroCompletedRef.current) {
      log('Tracking intro response ID', {
        responseId: message.response.id,
      });
      introResponseIdRef.current = message.response.id;

      introTimeoutRef.current = setTimeout(() => {
        log('!!!! Intro timeout triggered - forcing completion');
        if (!isIntroCompletedRef.current) {
          onSkipIntro();
        }
      }, CONSTANTS.INTRO_AUTO_COMPLETE_TIMEOUT_MS);
    }
  };

  const handleOutputItemDone = (message: any) => {
    log('Output Item Done', {
      itemId: message.item.id,
      responseId: message.response_id,
      role: message.item.role,
      isIntroResponse: message.response_id === introResponseIdRef.current,
    });

    if (message.item.role === 'assistant') {
      if (
        !isIntroCompletedRef.current &&
        message.response_id === introResponseIdRef.current
      ) {
        log('Marking intro message as complete');
        isIntroMessageCompleteRef.current = true;
      }
    }
  };

  const handleResponseDone = (message: any) => {
    log('Response Done', {
      responseId: message.response.id,
      isIntroResponse: message.response.id === introResponseIdRef.current,
      status: message.response.status,
    });
  };

  const handleAudioTranscriptionCompleted = (message: any) => {
    log('Audio Transcription Completed', { transcript: message.transcript });
    lastTranscriptRef.current = message.transcript.toLowerCase();
  };

  const handleConversationInterrupted = async () => {
    log('Conversation Interruption Detected');

    try {
      if (activeAudioTracksRef.current.size === 0) {
        log('Ignoring interruption - AI not speaking');
        return;
      }

      log('Interruption check result:', {
        activeAudioTracks: activeAudioTracksRef.current.size,
        transcript: lastTranscriptRef.current,
      });

    } catch (error) {
      log('Error handling interruption:', error);
    }
  };

  const handleConversationUpdated = async (message: any) => {
    const { item, delta } = message;
    log('Conversation Updated', {
      item,
      delta,
      introCompleted: isIntroCompletedRef.current,
    });

    if (delta?.audio) {
      const trackId = item.id;
      const isIntro = !isIntroCompletedRef.current;

      try {
        activeAudioTracksRef.current.add(trackId);
        updateAISpeakingState();

        log('Starting audio playback:', {
          trackId,
          isIntro,
          activeTracks: Array.from(activeAudioTracksRef.current),
        });

        if (!isIntroCompletedRef.current && !isSkipEnabledRef.current) {
          setTimeout(() => {
            console.log('Enabling skip after delay');
            isSkipEnabledRef.current = true;
          }, 1000);
        }

        // Play audio using Web Audio API
        const audioContext = new AudioContext();
        const source = audioContext.createBufferSource();
        audioContext.decodeAudioData(delta.audio, (buffer) => {
          source.buffer = buffer;
          source.connect(audioContext.destination);
          source.start();
          source.onended = async () => {
            log('Audio chunk completed:', {
              trackId,
              activeTracks: Array.from(activeAudioTracksRef.current),
            });
            activeAudioTracksRef.current.delete(trackId);
            updateAISpeakingState();
            
            // Only proceed if this was intro audio
            if (!isIntroCompletedRef.current && 
                message.response_id === introResponseIdRef.current) {
              await new Promise(resolve => setTimeout(resolve, 1000)); // Wait for any pending audio
              if (activeAudioTracksRef.current.size === 0) {
                isIntroCompletedRef.current = true;
                setIsIntroCompleted(true);
                await connectConversation();
              }
            }
          };
        });
      } catch (error) {
        console.log('Error processing audio:', error);
        activeAudioTracksRef.current.delete(trackId);
        updateAISpeakingState();
        setError('Audio playback error. Please try again.');
      }
    }
    setItems((prevItems) => [...prevItems, item]);
    itemsRef.current = [...itemsRef.current, item];
  };

  const handleError = (message: any) => {
    log('Error Occurred', message.error);
    setError(message.error.message || 'An unexpected error occurred');
  };

  const handleRetry = useCallback(() => {
    if (connectionAttempts < MAX_RETRY_ATTEMPTS) {
      connectConversation();
    } else {
      setConnectionAttempts(0);
      connectConversation();
    }
  }, [connectConversation, connectionAttempts]);

  const disconnectConversation = async (closedFeedback = false) => {
    log('disconnectConversation', items);
    setIsConnected(false);
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
    if (dataChannelRef.current) {
      dataChannelRef.current.close();
    }
    const duration = (Date.now() - startTimeRef.current) / 1000;
    log('Ending session:', { sessionId, duration });

    if (sessionId) {
      if (
        duration < MIN_DURATION_SECS &&
        userSlackId &&
        SLACK_ID_WHITELIST.includes(userSlackId)
      ) {
        setIsBadRequest(true);
        return;
      }

      log('Ending session, items:', items);
      log('Ending session, itemsRef:', itemsRef.current);

      const transcript = (items && items.length > 0 ? items : itemsRef.current)
        .map(
          (item) =>
            `${item.role}: ${item.formatted.text || item.formatted.transcript}`,
        )
        .join('\n');

      log('Ending session:', { sessionId, duration, transcript });
      endSession({
        sessionId,
        transcript,
        duration,
      });
    }
  };

  return {
    disconnectConversation,
    onSkipIntro,
    connectConversation,
    handleRetry,
    isConnected,
    isLoading,
    items,
    messages,
  };
};